import { useEffect } from 'react';

/**
 * Slide-in toast notification. Controlled by parent via `open`/`onClose`.
 */
export default function Toast({ open, onClose, title, message, variant = 'success', duration = 5000 }) {
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(onClose, duration);
    return () => clearTimeout(id);
  }, [open, onClose, duration]);

  if (!open) return null;

  const accent =
    variant === 'success'
      ? 'from-cyan/30 to-transparent border-cyan/40'
      : 'from-red-500/30 to-transparent border-red-400/40';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-3rem)] max-w-sm animate-toast-in"
    >
      <div className={`glass-strong flex items-start gap-3 rounded-2xl border bg-gradient-to-br ${accent} p-5 shadow-card`}>
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-cyan-sheen text-navy-900">
          {variant === 'success' ? (
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
              <path d="M5 10l3 3 7-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
              <path d="M10 5v5m0 3h.01M10 1a9 9 0 100 18 9 9 0 000-18z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold text-white">{title}</p>
          {message && <p className="mt-1 text-sm text-silver/80">{message}</p>}
        </div>
        <button
          onClick={onClose}
          aria-label="Dismiss"
          className="text-silver/60 transition-colors hover:text-white"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
